import { Dispatch, FC, SetStateAction } from 'react'
import { Box, IconButton, InputAdornment } from '@mui/material'
import { TrashIcon } from '@heroicons/react/24/outline'
import theme from 'theme'
import { ThemeButton } from 'ui/components/buttons'
import LabeledInputV2 from 'ui/components/form-components-v2/labeled-input-v2'
import ControlledCustomTrendsSelect from 'ui/components/form-components/ControlledCustomTrendsSelect'
import { VisibilityOffIcon, VisibilityOnIcon } from 'ui/components/svg'
import { ConnectionFormWrapper } from '../../jiraWizard.styles'

interface Props {
  formik: any
  schema: any[]
  visibleValues: Record<string, boolean>
  setVisibleValues: Dispatch<SetStateAction<Record<string, boolean>>>
  nameHelperText: string
  setNameHelperText: Dispatch<SetStateAction<string>>
  validateName: (value: string) => string
  disabled?: boolean
}

/* Connection Details step: the product spec's required connection properties,
 * plus the instance name and description. */
const ConnectionDetailsStep: FC<Props> = ({
  formik,
  schema,
  visibleValues,
  setVisibleValues,
  nameHelperText,
  setNameHelperText,
  validateName,
  disabled
}) => {
  const sortedSchema = [...schema].sort((a, b) => (a.order > b.order ? 1 : -1))

  const handleNameChange = (value: string) => {
    const message = validateName(value)
    setNameHelperText(message)
    formik.setFieldError('name', message || undefined)
  }

  /* Clears the credentials only — the instance's name and description survive. */
  const handleClearAll = () =>
    formik.setValues(
      Object.fromEntries(
        Object.entries(formik.values).map(([key, value]) => [
          key,
          key === 'name' || key === 'description' ? value : ''
        ])
      )
    )

  return (
    <ConnectionFormWrapper>
      <LabeledInputV2
        name="name"
        label="Name"
        value={formik.values.name}
        onChange={(e) => {
          formik.handleChange(e)
          handleNameChange(e.target.value)
        }}
        disabled={disabled}
        helperText={nameHelperText}
      />

      <LabeledInputV2
        name="description"
        label="Description (Optional)"
        value={formik.values.description}
        onChange={formik.handleChange}
        disabled={disabled}
      />

      {sortedSchema.map((property) => {
        const isSecret = !!property.airbyte_secret

        if (property.enum?.length) {
          return (
            <ControlledCustomTrendsSelect
              key={property.name}
              options={property.enum}
              placeholder={property.title}
              label="Region"
              labeltext={property.title}
              value={formik.values[property.name]}
              onChange={formik.handleChange}
              name={property.name}
              disabled={!!disabled}
              multiple={false}
              fullWidth
              autoComplete="off"
            />
          )
        }

        return (
          <LabeledInputV2
            data-private
            key={property.name}
            name={property.name}
            label={property.title}
            placeholder={`Enter ${property.title}`}
            autoComplete="off"
            value={formik.values[property.name]}
            onChange={formik.handleChange}
            type={
              property.type === 'integer' ? 'number' : isSecret && !visibleValues[property.name] ? 'password' : 'text'
            }
            endAdornment={
              isSecret && (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={`toggle ${property.title} visibility`}
                    onClick={() => setVisibleValues((prev) => ({ ...prev, [property.name]: !prev[property.name] }))}
                    edge="end"
                  >
                    {visibleValues[property.name] ? <VisibilityOnIcon /> : <VisibilityOffIcon />}
                  </IconButton>
                </InputAdornment>
              )
            }
            disabled={disabled}
          />
        )
      })}

      <Box className="clear-all-row">
        <ThemeButton
          themevariant="hollow"
          type="button"
          onClick={handleClearAll}
          disabled={disabled}
          startIcon={<TrashIcon width={16} height={16} />}
          sx={{
            fontWeight: `${theme.typography.fontWeight.extraThick} !important`,
            lineHeight: '22px !important',
            svg: { path: { fill: 'none !important', stroke: 'currentColor' } }
          }}
        >
          Clear All
        </ThemeButton>
      </Box>
    </ConnectionFormWrapper>
  )
}

export default ConnectionDetailsStep
